import {
  type AddCardDisputeEvidenceRequest,
  type CardDispute,
  CardDisputeAccountStatus,
  type CardDisputeDocumentEvidenceInput,
  type CardDisputeEligibilityRequest,
  CardDisputeMerchandiseOrServices,
  CardDisputeMerchantContactMethod,
  type CardDisputeReasonDetails,
  CardDisputeWorkflow,
  type CreateCardDisputeRequest,
} from '@column/api';
import { Currencies } from '@column/column-ui-kit';

import { type CardDisputeReason } from '~/lib/disputes/labels';

/**
 * Reason-questionnaire state, serialization, and readiness for dispute initiation and draft
 * repair. Pure logic: no React and no API calls, so the new-dispute wizard and the draft-repair
 * panel (and Part 05's repair flows) share one set of rules.
 *
 * The backend mirrors live in `pkg/cards/models/card_dispute_reason_details.go`; eligibility and
 * submit validation live in `pkg/cards/disputes/api.go`. Keep the two in sync.
 */

type NotAsDescribedDetails = NonNullable<CardDisputeReasonDetails['not_as_described']>;
type MerchantReturnRefusalReason = NonNullable<NonNullable<NotAsDescribedDetails['merchant_return_refusal_reason']>>;
type FraudType = NonNullable<NonNullable<CardDisputeReasonDetails['fraud']>['fraud_type']>;

export type { FraudType, MerchantReturnRefusalReason };

/** Whether a dispute reason requires a cardholder narrative at submission. Mirrors the backend. */
export const REASON_REQUIRES_NARRATIVE: Record<CardDisputeReason, boolean> = {
  fraud: false,
  no_authorization: false,
  incorrect_amount: false,
  incorrect_account_number: false,
  merchandise_services_not_received: true,
  canceled_recurring_transaction: true,
  not_as_described: true,
  counterfeit_merchandise: true,
  misrepresentation: true,
  credit_not_processed: true,
  non_receipt_cash_at_atm: true,
  other: true,
};

/** Whether a dispute reason requires at least one uploaded document at submission. Backend: counterfeit only. */
export const REASON_REQUIRES_DOCUMENT: Record<CardDisputeReason, boolean> = {
  fraud: false,
  no_authorization: false,
  incorrect_amount: false,
  incorrect_account_number: false,
  merchandise_services_not_received: false,
  canceled_recurring_transaction: false,
  not_as_described: false,
  counterfeit_merchandise: true,
  misrepresentation: false,
  credit_not_processed: false,
  non_receipt_cash_at_atm: false,
  other: false,
};

export const REASON_WORKFLOW: Record<CardDisputeReason, CardDisputeWorkflow> = {
  fraud: CardDisputeWorkflow.ALLOCATION,
  no_authorization: CardDisputeWorkflow.ALLOCATION,
  incorrect_amount: CardDisputeWorkflow.COLLABORATION,
  incorrect_account_number: CardDisputeWorkflow.COLLABORATION,
  merchandise_services_not_received: CardDisputeWorkflow.COLLABORATION,
  canceled_recurring_transaction: CardDisputeWorkflow.COLLABORATION,
  not_as_described: CardDisputeWorkflow.COLLABORATION,
  counterfeit_merchandise: CardDisputeWorkflow.COLLABORATION,
  misrepresentation: CardDisputeWorkflow.COLLABORATION,
  credit_not_processed: CardDisputeWorkflow.COLLABORATION,
  non_receipt_cash_at_atm: CardDisputeWorkflow.COLLABORATION,
  other: CardDisputeWorkflow.COLLABORATION,
};

/** Reasons whose questionnaire collects no reason-specific details. */
export const REASON_WITHOUT_DETAILS: CardDisputeReason[] = [
  'incorrect_account_number',
  'misrepresentation',
  'non_receipt_cash_at_atm',
  'other',
];

// Visa limits on canceled-recurring merchant contact details, from ROLSIRT.xsd via the backend.
// Counts are BYTES, not JavaScript characters, so multi-byte input fails earlier than length.
export const MERCHANT_CONTACT_DETAIL_LIMITS = {
  merchant_contact_phone_number: 20,
  merchant_contact_email_address: 64,
  merchant_contact_physical_address: 50,
  merchant_contact_application_name: 200,
} as const;

export type MerchantContactDetailField = keyof typeof MERCHANT_CONTACT_DETAIL_LIMITS;

export const MAX_REASON_FOR_PARTIAL_DISPUTE_LENGTH = 10_000; // Unicode characters, per the backend.

const byteLength = (value: string): number => new TextEncoder().encode(value).length;

const trimmed = (value: string | undefined): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  const next = value.trim();
  return next === '' ? undefined : next;
};

/** Fractional digits for a currency from the canonical currency metadata. `undefined` for codes the repository does not know: callers must fail safe rather than assume two decimals. */
export const currencyDecimalDigits = (currencyCode: string): number | undefined =>
  Currencies.find((currency) => currency.code === currencyCode)?.decimalDigits;

/**
 * Currency-aware major-unit (decimal string) → smallest-unit (integer) conversion using string
 * math, so money values never pass through a float. Returns undefined for input the currency
 * cannot represent (too many fractional digits), non-numeric input, or an unknown currency —
 * never a silently mis-scaled amount.
 */
export const parseMajorUnitsToSmallestUnit = (value: string | undefined, currencyCode: string): number | undefined => {
  const cleaned = (value ?? '').trim().replace(/,/g, '');
  if (cleaned === '' || !/^\d+(\.\d+)?$/.test(cleaned)) {
    return undefined;
  }

  const decimalDigits = currencyDecimalDigits(currencyCode);
  if (decimalDigits === undefined) {
    return undefined;
  }
  const [wholePart, fractionalPart = ''] = cleaned.split('.');
  if (fractionalPart.length > decimalDigits) {
    return undefined;
  }

  const digits = `${wholePart === '0' ? '' : wholePart}${fractionalPart.padEnd(decimalDigits, '0')}`;
  const parsed = Number(digits);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
};

/**
 * Smallest-unit (integer) → major-unit decimal string for form inputs, using string math so no
 * floating-point value is ever constructed from money.
 */
export const formatSmallestUnitToMajorUnits = (amount: number | undefined, currencyCode: string): string => {
  if (amount === undefined || !Number.isSafeInteger(amount) || amount <= 0) {
    return '';
  }
  const decimalDigits = currencyDecimalDigits(currencyCode);
  if (decimalDigits === undefined) {
    return '';
  }
  const digits = amount.toString().padStart(decimalDigits + 1, '0');
  const whole = digits.slice(0, digits.length - decimalDigits);
  const fraction = decimalDigits > 0 ? `.${digits.slice(digits.length - decimalDigits)}` : '';
  return `${whole}${fraction}`;
};

/**
 * Questionnaire form state. `false` is a valid supplied answer for every boolean and is never
 * conflated with unanswered (`undefined`); factual statements are never prefilled affirmatively.
 * Field values use the generated API enum types.
 */
export interface DisputeReasonFormState {
  fraud?: { fraud_type?: FraudType };
  no_authorization?: { account_status?: CardDisputeAccountStatus };
  /** Major-unit decimal string in the dispute currency, e.g. `"19.99"`. */
  incorrect_amount?: { cardholder_receipt_amount?: string };
  merchandise_services_not_received?: {
    merchandise_or_services?: string;
    order_details?: string;
    /** ISO date `yyyy-mm-dd`. */
    expected_receipt_date?: string;
    cardholder_attempted_resolution?: boolean;
  };
  canceled_recurring_transaction?: {
    cancellation_date?: string;
    cancellation_method?: string;
    merchant_contact_methods: CardDisputeMerchantContactMethod[];
    merchant_contact_phone_number?: string;
    merchant_contact_email_address?: string;
    merchant_contact_physical_address?: string;
    merchant_contact_application_name?: string;
    account_closure_date?: string;
  };
  not_as_described?: {
    merchandise_or_services?: string;
    order_details?: string;
    received_date?: string;
    cardholder_attempted_resolution?: boolean;
    cardholder_returned_merchandise?: boolean;
    cardholder_attempted_return?: boolean;
    attempted_return_date?: string;
    return_attempt_details?: string;
    merchandise_disposition?: string;
    merchant_return_refusal_reason?: MerchantReturnRefusalReason;
  };
  counterfeit_merchandise?: {
    order_details?: string;
    counterfeit_discovered_date?: string;
    counterfeit_details?: string;
    merchandise_disposition?: string;
  };
  credit_not_processed?: { credit_voucher_date?: string };
}

export const emptyReasonFormState = (): DisputeReasonFormState => ({
  canceled_recurring_transaction: { merchant_contact_methods: [] },
});

/** Machine-readable identifiers for missing required questionnaire answers, used for tests and errors. */
export type ReasonFieldKey = string;

const hasText = (value: string | undefined): boolean => trimmed(value) !== undefined;

const hasDate = (value: string | undefined): boolean => /^\d{4}-\d{2}-\d{2}$/.test((value ?? '').trim());

const missingMerchandiseServicesFields = (
  details: NonNullable<DisputeReasonFormState['merchandise_services_not_received']>
): ReasonFieldKey[] => {
  const missing: ReasonFieldKey[] = [];
  if (!details.merchandise_or_services) missing.push('merchandise_or_services');
  if (!hasText(details.order_details)) missing.push('order_details');
  if (!hasDate(details.expected_receipt_date)) missing.push('expected_receipt_date');
  if (details.cardholder_attempted_resolution === undefined) missing.push('cardholder_attempted_resolution');
  return missing;
};

const missingCanceledRecurringFields = (
  details: NonNullable<DisputeReasonFormState['canceled_recurring_transaction']>
): ReasonFieldKey[] => {
  const missing: ReasonFieldKey[] = [];
  if (!hasDate(details.cancellation_date)) missing.push('cancellation_date');
  if (!hasText(details.cancellation_method)) missing.push('cancellation_method');
  if (details.merchant_contact_methods.length === 0) {
    missing.push('merchant_contact_methods');
    return missing;
  }

  // Each reported channel requires its detail; several channels share one field.
  const methods = details.merchant_contact_methods;
  const requireDetail = (fields: MerchantContactDetailField[], applies: boolean) => {
    if (!applies) {
      return;
    }
    fields.forEach((field) => {
      if (!hasText(details[field]) && !missing.includes(field)) {
        missing.push(field);
      }
    });
  };
  requireDetail(
    ['merchant_contact_phone_number'],
    methods.some((m) => m === 'call_center' || m === 'sms')
  );
  requireDetail(['merchant_contact_email_address'], methods.includes(CardDisputeMerchantContactMethod.EMAIL));
  requireDetail(
    ['merchant_contact_physical_address'],
    methods.some((m) => m === 'mail' || m === 'in_person')
  );
  requireDetail(['merchant_contact_application_name'], methods.includes(CardDisputeMerchantContactMethod.WEBFORM));
  return missing;
};

const missingNotAsDescribedFields = (
  details: NonNullable<DisputeReasonFormState['not_as_described']>
): ReasonFieldKey[] => {
  const missing: ReasonFieldKey[] = [];
  if (!details.merchandise_or_services) missing.push('merchandise_or_services');
  if (!hasText(details.order_details)) missing.push('order_details');
  if (!hasDate(details.received_date)) missing.push('received_date');
  if (details.cardholder_attempted_resolution === undefined) missing.push('cardholder_attempted_resolution');
  if (details.cardholder_returned_merchandise === undefined) missing.push('cardholder_returned_merchandise');

  // VROL requires an explicit attempted-return answer once the merchandise was not returned.
  if (details.cardholder_returned_merchandise === false && details.cardholder_attempted_return === undefined) {
    missing.push('cardholder_attempted_return');
  }

  // Detailed attempted-return fields: merchandise (not services), not returned, return attempted.
  if (
    details.merchandise_or_services === 'merchandise' &&
    details.cardholder_returned_merchandise === false &&
    details.cardholder_attempted_return === true
  ) {
    if (!hasDate(details.attempted_return_date)) missing.push('attempted_return_date');
    if (!hasText(details.return_attempt_details)) missing.push('return_attempt_details');
    if (!hasText(details.merchandise_disposition)) missing.push('merchandise_disposition');
    if (!details.merchant_return_refusal_reason) missing.push('merchant_return_refusal_reason');
  }

  return missing;
};

const missingCounterfeitFields = (
  details: NonNullable<DisputeReasonFormState['counterfeit_merchandise']>
): ReasonFieldKey[] => {
  const missing: ReasonFieldKey[] = [];
  if (!hasText(details.order_details)) missing.push('order_details');
  if (!hasDate(details.counterfeit_discovered_date)) missing.push('counterfeit_discovered_date');
  if (!hasText(details.counterfeit_details)) missing.push('counterfeit_details');
  if (!hasText(details.merchandise_disposition)) missing.push('merchandise_disposition');
  return missing;
};

/**
 * Required questionnaire answers that are missing for the selected reason. Unknown enum values are
 * treated as missing so the form never reports readiness on a value the backend would reject.
 */
export const missingReasonFields = (
  reason: CardDisputeReason,
  state: DisputeReasonFormState,
  currency: string
): ReasonFieldKey[] => {
  switch (reason) {
    case 'fraud':
      return state.fraud?.fraud_type ? [] : ['fraud_type'];
    case 'no_authorization':
      return state.no_authorization?.account_status ? [] : ['account_status'];
    case 'incorrect_amount': {
      const receipt = parseMajorUnitsToSmallestUnit(state.incorrect_amount?.cardholder_receipt_amount, currency);
      return receipt !== undefined && receipt > 0 ? [] : ['cardholder_receipt_amount'];
    }
    case 'merchandise_services_not_received':
      return state.merchandise_services_not_received
        ? missingMerchandiseServicesFields(state.merchandise_services_not_received)
        : ['merchandise_or_services'];
    case 'canceled_recurring_transaction':
      return state.canceled_recurring_transaction
        ? missingCanceledRecurringFields(state.canceled_recurring_transaction)
        : ['cancellation_date'];
    case 'not_as_described':
      return state.not_as_described ? missingNotAsDescribedFields(state.not_as_described) : ['merchandise_or_services'];
    case 'counterfeit_merchandise':
      return state.counterfeit_merchandise
        ? missingCounterfeitFields(state.counterfeit_merchandise)
        : ['order_details'];
    case 'credit_not_processed':
      return hasDate(state.credit_not_processed?.credit_voucher_date) ? [] : ['credit_voucher_date'];
    default:
      return [];
  }
};

/** The first over-limit canceled-recurring contact detail, if any. Byte-counted, not character-counted. */
export const findOversizedContactDetail = (state: DisputeReasonFormState): MerchantContactDetailField | undefined => {
  const details = state.canceled_recurring_transaction;
  if (!details) {
    return undefined;
  }
  const entries: [MerchantContactDetailField, string | undefined][] = [
    ['merchant_contact_phone_number', details.merchant_contact_phone_number],
    ['merchant_contact_email_address', details.merchant_contact_email_address],
    ['merchant_contact_physical_address', details.merchant_contact_physical_address],
    ['merchant_contact_application_name', details.merchant_contact_application_name],
  ];
  return entries.find(
    ([field, value]) => value !== undefined && byteLength(value) > MERCHANT_CONTACT_DETAIL_LIMITS[field]
  )?.[0];
};

/**
 * Serialize only the selected reason's sub-object. When the selected reason changes, no other
 * reason's detail object and no stale hidden answer is ever sent.
 */
export const buildReasonDetailsPayload = (
  reason: CardDisputeReason,
  state: DisputeReasonFormState,
  currency: string
): CardDisputeReasonDetails | undefined => {
  switch (reason) {
    case 'fraud':
      return state.fraud?.fraud_type ? { fraud: { fraud_type: state.fraud.fraud_type } } : undefined;
    case 'no_authorization':
      return state.no_authorization?.account_status
        ? { no_authorization: { account_status: state.no_authorization.account_status } }
        : undefined;
    case 'incorrect_amount': {
      const receiptAmount = parseMajorUnitsToSmallestUnit(state.incorrect_amount?.cardholder_receipt_amount, currency);
      return receiptAmount !== undefined && receiptAmount > 0
        ? { incorrect_amount: { cardholder_receipt_amount: receiptAmount } }
        : undefined;
    }
    case 'merchandise_services_not_received': {
      const details = state.merchandise_services_not_received;
      if (!details) {
        return undefined;
      }
      const payload: NonNullable<CardDisputeReasonDetails['merchandise_services_not_received']> = {};
      if (details.merchandise_or_services) {
        payload.merchandise_or_services = details.merchandise_or_services as CardDisputeMerchandiseOrServices;
      }
      const orderDetails = trimmed(details.order_details);
      if (orderDetails !== undefined) payload.order_details = orderDetails;
      if (hasDate(details.expected_receipt_date)) payload.expected_receipt_date = details.expected_receipt_date!.trim();
      if (details.cardholder_attempted_resolution !== undefined) {
        payload.cardholder_attempted_resolution = details.cardholder_attempted_resolution;
      }
      return { merchandise_services_not_received: payload };
    }
    case 'canceled_recurring_transaction': {
      const details = state.canceled_recurring_transaction;
      if (!details) {
        return undefined;
      }
      const payload: NonNullable<CardDisputeReasonDetails['canceled_recurring_transaction']> = {};
      if (hasDate(details.cancellation_date)) payload.cancellation_date = details.cancellation_date!.trim();
      const cancellationMethod = trimmed(details.cancellation_method);
      if (cancellationMethod !== undefined) payload.cancellation_method = cancellationMethod;
      if (details.merchant_contact_methods.length > 0) {
        payload.merchant_contact_methods = details.merchant_contact_methods;
      }
      const phoneNumber = trimmed(details.merchant_contact_phone_number);
      if (phoneNumber !== undefined) payload.merchant_contact_phone_number = phoneNumber;
      const emailAddress = trimmed(details.merchant_contact_email_address);
      if (emailAddress !== undefined) payload.merchant_contact_email_address = emailAddress;
      const physicalAddress = trimmed(details.merchant_contact_physical_address);
      if (physicalAddress !== undefined) payload.merchant_contact_physical_address = physicalAddress;
      const applicationName = trimmed(details.merchant_contact_application_name);
      if (applicationName !== undefined) payload.merchant_contact_application_name = applicationName;
      if (hasDate(details.account_closure_date)) payload.account_closure_date = details.account_closure_date!.trim();
      return { canceled_recurring_transaction: payload };
    }
    case 'not_as_described': {
      const details = state.not_as_described;
      if (!details) {
        return undefined;
      }
      const payload: NotAsDescribedDetails = {};
      if (details.merchandise_or_services) {
        payload.merchandise_or_services = details.merchandise_or_services as NonNullable<
          NotAsDescribedDetails['merchandise_or_services']
        >;
      }
      const orderDetails = trimmed(details.order_details);
      if (orderDetails !== undefined) payload.order_details = orderDetails;
      if (hasDate(details.received_date)) payload.received_date = details.received_date!.trim();
      if (details.cardholder_attempted_resolution !== undefined) {
        payload.cardholder_attempted_resolution = details.cardholder_attempted_resolution;
      }
      if (details.cardholder_returned_merchandise !== undefined) {
        payload.cardholder_returned_merchandise = details.cardholder_returned_merchandise;
      }
      if (details.cardholder_attempted_return !== undefined) {
        payload.cardholder_attempted_return = details.cardholder_attempted_return;
      }
      if (hasDate(details.attempted_return_date)) payload.attempted_return_date = details.attempted_return_date!.trim();
      const returnAttemptDetails = trimmed(details.return_attempt_details);
      if (returnAttemptDetails !== undefined) payload.return_attempt_details = returnAttemptDetails;
      const merchandiseDisposition = trimmed(details.merchandise_disposition);
      if (merchandiseDisposition !== undefined) payload.merchandise_disposition = merchandiseDisposition;
      if (details.merchant_return_refusal_reason) {
        payload.merchant_return_refusal_reason = details.merchant_return_refusal_reason;
      }
      return { not_as_described: payload };
    }
    case 'counterfeit_merchandise': {
      const details = state.counterfeit_merchandise;
      if (!details) {
        return undefined;
      }
      const payload: NonNullable<CardDisputeReasonDetails['counterfeit_merchandise']> = {};
      const orderDetails = trimmed(details.order_details);
      if (orderDetails !== undefined) payload.order_details = orderDetails;
      if (hasDate(details.counterfeit_discovered_date)) {
        payload.counterfeit_discovered_date = details.counterfeit_discovered_date!.trim();
      }
      const counterfeitDetails = trimmed(details.counterfeit_details);
      if (counterfeitDetails !== undefined) payload.counterfeit_details = counterfeitDetails;
      const merchandiseDisposition = trimmed(details.merchandise_disposition);
      if (merchandiseDisposition !== undefined) payload.merchandise_disposition = merchandiseDisposition;
      return { counterfeit_merchandise: payload };
    }
    case 'credit_not_processed': {
      const details = state.credit_not_processed;
      if (!details || !hasDate(details.credit_voucher_date)) {
        return undefined;
      }
      return { credit_not_processed: { credit_voucher_date: details.credit_voucher_date!.trim() } };
    }
    default:
      return undefined;
  }
};

/** Per-reason form state reconstructed from a dispute's stored reason details, for draft repair. */
export const reasonFormStateFromDetails = (
  reason: CardDisputeReason,
  details: CardDisputeReasonDetails | undefined,
  currency: string
): DisputeReasonFormState => {
  switch (reason) {
    case 'fraud':
      return { fraud: { fraud_type: details?.fraud?.fraud_type } };
    case 'no_authorization':
      return { no_authorization: { account_status: details?.no_authorization?.account_status } };
    case 'incorrect_amount':
      return {
        incorrect_amount: {
          cardholder_receipt_amount: formatSmallestUnitToMajorUnits(
            details?.incorrect_amount?.cardholder_receipt_amount,
            currency
          ),
        },
      };
    case 'merchandise_services_not_received':
      return {
        merchandise_services_not_received: {
          merchandise_or_services: details?.merchandise_services_not_received?.merchandise_or_services,
          order_details: details?.merchandise_services_not_received?.order_details,
          expected_receipt_date: details?.merchandise_services_not_received?.expected_receipt_date,
          cardholder_attempted_resolution: details?.merchandise_services_not_received?.cardholder_attempted_resolution,
        },
      };
    case 'canceled_recurring_transaction':
      return {
        canceled_recurring_transaction: {
          merchant_contact_methods: [...(details?.canceled_recurring_transaction?.merchant_contact_methods ?? [])],
          cancellation_date: details?.canceled_recurring_transaction?.cancellation_date,
          cancellation_method: details?.canceled_recurring_transaction?.cancellation_method,
          merchant_contact_phone_number: details?.canceled_recurring_transaction?.merchant_contact_phone_number,
          merchant_contact_email_address: details?.canceled_recurring_transaction?.merchant_contact_email_address,
          merchant_contact_physical_address: details?.canceled_recurring_transaction?.merchant_contact_physical_address,
          merchant_contact_application_name: details?.canceled_recurring_transaction?.merchant_contact_application_name,
          account_closure_date: details?.canceled_recurring_transaction?.account_closure_date,
        },
      };
    case 'not_as_described':
      return {
        not_as_described: {
          merchandise_or_services: details?.not_as_described?.merchandise_or_services,
          order_details: details?.not_as_described?.order_details,
          received_date: details?.not_as_described?.received_date,
          cardholder_attempted_resolution: details?.not_as_described?.cardholder_attempted_resolution,
          cardholder_returned_merchandise: details?.not_as_described?.cardholder_returned_merchandise,
          cardholder_attempted_return: details?.not_as_described?.cardholder_attempted_return,
          attempted_return_date: details?.not_as_described?.attempted_return_date,
          return_attempt_details: details?.not_as_described?.return_attempt_details,
          merchandise_disposition: details?.not_as_described?.merchandise_disposition,
          merchant_return_refusal_reason: details?.not_as_described?.merchant_return_refusal_reason,
        },
      };
    case 'counterfeit_merchandise':
      return {
        counterfeit_merchandise: {
          order_details: details?.counterfeit_merchandise?.order_details,
          counterfeit_discovered_date: details?.counterfeit_merchandise?.counterfeit_discovered_date,
          counterfeit_details: details?.counterfeit_merchandise?.counterfeit_details,
          merchandise_disposition: details?.counterfeit_merchandise?.merchandise_disposition,
        },
      };
    case 'credit_not_processed':
      return {
        credit_not_processed: { credit_voucher_date: details?.credit_not_processed?.credit_voucher_date },
      };
    default:
      return emptyReasonFormState();
  }
};

/**
 * Submission readiness for a dispute being edited (new or resumed draft). `missing` carries
 * human-readable gaps so the UI can explain what a submit would fail on without calling it.
 */
export interface DisputeSubmissionReadiness {
  ready: boolean;
  missing: string[];
}

export const disputeSubmissionReadiness = (input: {
  reason: CardDisputeReason;
  formState: DisputeReasonFormState;
  currency: string;
  /** Narrative typed in the current session; already-attached narrative evidence counts separately. */
  narrative?: string;
  /** Narrative evidence already attached to the dispute (resumed drafts). */
  hasAttachedNarrativeEvidence?: boolean;
  /** Document evidence selected in the current session. */
  evidence: CardDisputeDocumentEvidenceInput[];
  /** Document evidence already attached to the dispute (resumed drafts). */
  attachedDocumentCount?: number;
  /** Whether a partial-claim explanation is required: claim below the original settled amount. */
  requiresPartialExplanation?: boolean;
  partialExplanation?: string;
}): DisputeSubmissionReadiness => {
  const missing: string[] = [];

  if (missingReasonFields(input.reason, input.formState, input.currency).length > 0) {
    missing.push('required questionnaire answers');
  }
  if (
    REASON_REQUIRES_NARRATIVE[input.reason] &&
    !input.hasAttachedNarrativeEvidence &&
    trimmed(input.narrative) === undefined
  ) {
    missing.push('a written statement describing the problem');
  }
  if (
    REASON_REQUIRES_DOCUMENT[input.reason] &&
    (input.attachedDocumentCount ?? 0) + (input.evidence ?? []).length === 0
  ) {
    missing.push('at least one supporting document');
  }
  if (input.requiresPartialExplanation && trimmed(input.partialExplanation) === undefined) {
    missing.push('an explanation of why the claim is less than the full transaction amount');
  }

  return { ready: missing.length === 0, missing };
};

/** The explanation limit is Unicode characters, not bytes, per the backend. */
export const reasonForPartialDisputeTooLong = (value: string | undefined): boolean =>
  value !== undefined && [...value].length > MAX_REASON_FOR_PARTIAL_DISPUTE_LENGTH;

export interface DisputeFormPayload {
  reason: CardDisputeReason;
  reason_details?: CardDisputeReasonDetails;
  cardholder_narrative?: string;
  evidence?: CardDisputeDocumentEvidenceInput[];
  reason_for_partial_dispute?: string;
}

/**
 * Build the shared request payload for eligibility checks and creation. Narrative and evidence are
 * included only when supplied; creation adds amount/transaction/currency and the credit flag.
 */
export const buildDisputeFormPayload = (input: {
  reason: CardDisputeReason;
  formState: DisputeReasonFormState;
  currency: string;
  narrative?: string;
  evidence: CardDisputeDocumentEvidenceInput[];
  partialExplanation?: string;
}): DisputeFormPayload => {
  const payload: DisputeFormPayload = { reason: input.reason };
  const reasonDetails = buildReasonDetailsPayload(input.reason, input.formState, input.currency);
  if (reasonDetails) {
    payload.reason_details = reasonDetails;
  }
  const narrative = trimmed(input.narrative);
  if (narrative !== undefined) {
    payload.cardholder_narrative = narrative;
  }
  const evidence = input.evidence ?? [];
  if (evidence.length > 0) {
    payload.evidence = evidence;
  }
  const partialExplanation = trimmed(input.partialExplanation);
  if (partialExplanation !== undefined) {
    payload.reason_for_partial_dispute = partialExplanation;
  }
  return payload;
};

/** The eligibility request for the current form state. A passing eligibility call alone does not prove the submit request is complete (it does not carry the partial explanation). */
export const buildEligibilityRequest = (input: {
  cardTransactionId: string;
  amount: number;
  currency: string;
  reason: CardDisputeReason;
  formState: DisputeReasonFormState;
  narrative?: string;
  evidence: CardDisputeDocumentEvidenceInput[];
}): CardDisputeEligibilityRequest => {
  const { reason, reason_details, cardholder_narrative, evidence } = buildDisputeFormPayload({
    reason: input.reason,
    formState: input.formState,
    currency: input.currency,
    narrative: input.narrative,
    evidence: input.evidence,
  });

  const request: CardDisputeEligibilityRequest = {
    card_transaction_id: input.cardTransactionId,
    amount: input.amount,
    currency: input.currency,
    reason,
  };
  if (reason_details) request.reason_details = reason_details;
  if (cardholder_narrative) request.cardholder_narrative = cardholder_narrative;
  if (evidence) request.evidence = evidence;
  return request;
};

/**
 * The create request for the wizard's draft-first flow. Always `submit: false`: network submission
 * is a separate deliberate action, never combined with a draft save. The partial explanation is
 * only carried for partial claims, and the credit flag is sent exactly as confirmed.
 */
export const buildCreateDisputeRequest = (input: {
  cardTransactionId: string;
  claimAmount: number;
  currency: string;
  reason: CardDisputeReason;
  formState: DisputeReasonFormState;
  narrative?: string;
  evidence: CardDisputeDocumentEvidenceInput[];
  partial: boolean;
  partialExplanation?: string;
  issueProvisionalCredit: boolean;
}): CreateCardDisputeRequest => {
  const payload = buildDisputeFormPayload({
    reason: input.reason,
    formState: input.formState,
    currency: input.currency,
    narrative: input.narrative,
    evidence: input.evidence,
    partialExplanation: input.partial ? input.partialExplanation : undefined,
  });

  const request: CreateCardDisputeRequest = {
    card_transaction_id: input.cardTransactionId,
    amount: input.claimAmount,
    currency: input.currency,
    reason: payload.reason,
    issue_provisional_credit: input.issueProvisionalCredit,
    submit: false,
  };
  if (payload.reason_details) request.reason_details = payload.reason_details;
  if (payload.cardholder_narrative) request.cardholder_narrative = payload.cardholder_narrative;
  if (payload.evidence) request.evidence = payload.evidence;
  if (payload.reason_for_partial_dispute) request.reason_for_partial_dispute = payload.reason_for_partial_dispute;
  return request;
};

/**
 * The ordered `/evidence` calls that persist draft corrections: reason details first, then the
 * partial explanation, then one call per new narrative/document. Strings are trimmed; blank
 * changes are dropped. Empty-string updates are never sent — the backend merge cannot clear a
 * stored string, so corrections replace rather than erase.
 */
export const buildDraftRepairCalls = (input: {
  reason: CardDisputeReason;
  currency: string;
  storedDetails: CardDisputeReasonDetails | undefined;
  formState: DisputeReasonFormState;
  storedPartialExplanation: string | undefined;
  partialExplanationVisible: boolean;
  nextPartialExplanation: string;
  narrative?: string;
  evidence: CardDisputeDocumentEvidenceInput[];
}): AddCardDisputeEvidenceRequest[] => {
  const calls: AddCardDisputeEvidenceRequest[] = [];

  const storedPayload = buildReasonDetailsPayload(
    input.reason,
    reasonFormStateFromDetails(input.reason, input.storedDetails, input.currency),
    input.currency
  );
  const nextDetails = buildReasonDetailsPayload(input.reason, input.formState, input.currency);
  if (nextDetails && JSON.stringify(storedPayload) !== JSON.stringify(nextDetails)) {
    calls.push({ reason_details: nextDetails });
  }

  const storedPartial = (input.storedPartialExplanation ?? '').trim();
  const nextPartial = input.nextPartialExplanation.trim();
  if (input.partialExplanationVisible && nextPartial !== '' && storedPartial !== nextPartial) {
    calls.push({ reason_for_partial_dispute: nextPartial });
  }

  const narrative = trimmed(input.narrative);
  if (narrative !== undefined) {
    calls.push({ narrative });
  }

  (input.evidence ?? []).forEach((document) => {
    calls.push({ document_id: document.document_id, evidence_type: document.evidence_type });
  });

  return calls;
};

/** Whether the current claim is partial: below the original settled amount for the transaction. */
export const isPartialClaim = (claimAmount: number, originalSettledAmount: number): boolean =>
  claimAmount < originalSettledAmount;

/** Whether an incorrect-amount claim exceeds the settled amount minus the cardholder receipt amount. */
export const exceedsReceiptDifference = (input: {
  claimAmount: number;
  settledAmount: number;
  receiptAmount: number | undefined;
}): boolean => {
  if (input.receiptAmount === undefined) {
    return false;
  }
  return input.claimAmount > input.settledAmount - input.receiptAmount;
};

/**
 * Human-readable questionnaire answers for the review step: exactly what would be serialized for
 * the selected reason, labeled. Only supplied answers appear.
 */
export const questionnaireAnswers = (
  reason: CardDisputeReason,
  state: DisputeReasonFormState,
  currency: string,
  labels: {
    fraudType: (value: string) => string;
    accountStatus: (value: string) => string;
    merchandiseOrServices: (value: string) => string;
    contactMethod: (value: string) => string;
    returnRefusalReason: (value: string) => string;
  }
): { label: string; answer: string }[] => {
  const answers: { label: string; answer: string }[] = [];
  const yesNo = (value: boolean | undefined): string => (value === undefined ? '' : value ? 'Yes' : 'No');
  const dateOnly = (value: string | undefined): string => (hasDate(value) ? value!.trim() : '');

  switch (reason) {
    case 'fraud':
      if (state.fraud?.fraud_type) {
        answers.push({ label: 'Fraud type', answer: labels.fraudType(state.fraud.fraud_type) });
      }
      break;
    case 'no_authorization':
      if (state.no_authorization?.account_status) {
        answers.push({ label: 'Account status', answer: labels.accountStatus(state.no_authorization.account_status) });
      }
      break;
    case 'incorrect_amount': {
      const receipt = parseMajorUnitsToSmallestUnit(state.incorrect_amount?.cardholder_receipt_amount, currency);
      if (receipt !== undefined && receipt > 0) {
        answers.push({
          label: "Amount on the cardholder's receipt",
          answer: `${state.incorrect_amount!.cardholder_receipt_amount} ${currency}`,
        });
      }
      break;
    }
    case 'merchandise_services_not_received': {
      const details = state.merchandise_services_not_received;
      if (!details) {
        break;
      }
      if (details.merchandise_or_services) {
        answers.push({ label: 'Purchased', answer: labels.merchandiseOrServices(details.merchandise_or_services) });
      }
      const orderDetails = trimmed(details.order_details);
      if (orderDetails) answers.push({ label: 'Order details', answer: orderDetails });
      if (dateOnly(details.expected_receipt_date)) {
        answers.push({ label: 'Expected receipt date', answer: dateOnly(details.expected_receipt_date) });
      }
      const attempted = yesNo(details.cardholder_attempted_resolution);
      if (attempted) answers.push({ label: 'Attempted resolution with the merchant', answer: attempted });
      break;
    }
    case 'canceled_recurring_transaction': {
      const details = state.canceled_recurring_transaction;
      if (!details) {
        break;
      }
      if (dateOnly(details.cancellation_date))
        answers.push({ label: 'Cancellation date', answer: dateOnly(details.cancellation_date) });
      const method = trimmed(details.cancellation_method);
      if (method) answers.push({ label: 'Cancellation method', answer: method });
      if (details.merchant_contact_methods.length > 0) {
        answers.push({
          label: 'Contact methods',
          answer: details.merchant_contact_methods.map(labels.contactMethod).join(', '),
        });
      }
      const phone = trimmed(details.merchant_contact_phone_number);
      if (phone) answers.push({ label: 'Phone number used', answer: phone });
      const email = trimmed(details.merchant_contact_email_address);
      if (email) answers.push({ label: 'Email address used', answer: email });
      const address = trimmed(details.merchant_contact_physical_address);
      if (address) answers.push({ label: 'Address used', answer: address });
      const application = trimmed(details.merchant_contact_application_name);
      if (application) answers.push({ label: 'App or website used', answer: application });
      if (dateOnly(details.account_closure_date)) {
        answers.push({ label: 'Account closure date', answer: dateOnly(details.account_closure_date) });
      }
      break;
    }
    case 'not_as_described': {
      const details = state.not_as_described;
      if (!details) {
        break;
      }
      if (details.merchandise_or_services) {
        answers.push({ label: 'Purchased', answer: labels.merchandiseOrServices(details.merchandise_or_services) });
      }
      const orderDetails = trimmed(details.order_details);
      if (orderDetails) answers.push({ label: 'Order details', answer: orderDetails });
      if (dateOnly(details.received_date))
        answers.push({ label: 'Date received', answer: dateOnly(details.received_date) });
      const attempted = yesNo(details.cardholder_attempted_resolution);
      if (attempted) answers.push({ label: 'Attempted resolution with the merchant', answer: attempted });
      const returned = yesNo(details.cardholder_returned_merchandise);
      if (returned) answers.push({ label: 'Merchandise returned', answer: returned });
      const attemptedReturn = yesNo(details.cardholder_attempted_return);
      if (details.cardholder_returned_merchandise === false && attemptedReturn) {
        answers.push({ label: 'Attempted return', answer: attemptedReturn });
      }
      if (dateOnly(details.attempted_return_date)) {
        answers.push({ label: 'Attempted return date', answer: dateOnly(details.attempted_return_date) });
      }
      const attemptDetails = trimmed(details.return_attempt_details);
      if (attemptDetails) answers.push({ label: 'Return attempt details', answer: attemptDetails });
      const disposition = trimmed(details.merchandise_disposition);
      if (disposition) answers.push({ label: 'Merchandise disposition', answer: disposition });
      if (details.merchant_return_refusal_reason) {
        answers.push({
          label: 'Merchant return refusal reason',
          answer: labels.returnRefusalReason(details.merchant_return_refusal_reason),
        });
      }
      break;
    }
    case 'counterfeit_merchandise': {
      const details = state.counterfeit_merchandise;
      if (!details) {
        break;
      }
      const orderDetails = trimmed(details.order_details);
      if (orderDetails) answers.push({ label: 'Order details', answer: orderDetails });
      if (dateOnly(details.counterfeit_discovered_date)) {
        answers.push({ label: 'Date discovered', answer: dateOnly(details.counterfeit_discovered_date) });
      }
      const counterfeitDetails = trimmed(details.counterfeit_details);
      if (counterfeitDetails) answers.push({ label: 'How counterfeiting was found', answer: counterfeitDetails });
      const disposition = trimmed(details.merchandise_disposition);
      if (disposition) answers.push({ label: 'Merchandise disposition', answer: disposition });
      break;
    }
    case 'credit_not_processed': {
      const voucherDate = dateOnly(state.credit_not_processed?.credit_voucher_date);
      if (voucherDate) answers.push({ label: 'Credit voucher date', answer: voucherDate });
      break;
    }
    default:
      break;
  }

  return answers;
};

/** Extract the dispute's reason as a typed CardDisputeReason, or undefined for unknown values. */
export const disputeReasonOf = (dispute: CardDispute): CardDisputeReason | undefined => {
  const reason = dispute.reason as CardDisputeReason;
  return Object.prototype.hasOwnProperty.call(REASON_REQUIRES_NARRATIVE, reason) ? reason : undefined;
};
